import React, { useEffect, useState } from 'react';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { DatePicker } from '../ui/DatePicker';
import { Button } from '../ui/Button';
import { BankFields } from './BankFields';
import { COAFormData, AccountType, AccountSubType } from '../../types/coa.types';
import { coaService } from '../../services/coa.service';
interface COAFormProps {
  initialData?: COAFormData;
  onSubmit: (data: COAFormData) => void;
  onCancel: () => void;
  isEdit?: boolean;
}
export function COAForm({
  initialData,
  onSubmit,
  onCancel,
  isEdit = false
}: COAFormProps) {
  const [formData, setFormData] = useState<COAFormData>(initialData || {
    accountCode: '',
    accountName: '',
    accountNameSomali: '',
    accountType: '',
    subType: '',
    openingDate: new Date().toISOString().split('T')[0],
    isInactive: false
  });
  const [errors, setErrors] = useState<Partial<Record<keyof COAFormData, string>>>({});
  const [parentAccounts, setParentAccounts] = useState<any[]>([]);
  useEffect(() => {
    loadParentAccounts();
  }, []);
  const loadParentAccounts = async () => {
    const accounts = await coaService.getAccounts();
    setParentAccounts(accounts.map(a => ({
      value: a.id,
      label: `${a.accountCode} - ${a.accountName}`
    })));
  };
  const accountTypes: AccountType[] = ['Asset', 'Liability', 'Equity', 'Expense', 'Income'];
  const subTypesByType: Record<AccountType, AccountSubType[]> = {
    Asset: ['Current Asset', 'Fixed Asset', 'Intangible Asset', 'Bank', 'Cash'],
    Liability: ['Current Liability', 'Long-term Liability'],
    Equity: ['Owner Equity', 'Retained Earnings'],
    Expense: ['Operating Expense', 'Non-operating Expense'],
    Income: ['Operating Income', 'Non-operating Income']
  };
  const handleParentChange = async (parentId: string) => {
    if (parentId) {
      const parent = await coaService.getAccountById(parentId);
      if (parent) {
        setFormData(prev => ({
          ...prev,
          parentId,
          accountType: parent.accountType,
          subType: parent.subType
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        parentId: '',
        accountType: '',
        subType: ''
      }));
    }
  };
  const validateForm = async (): Promise<boolean> => {
    const newErrors: Partial<Record<keyof COAFormData, string>> = {};
    if (!formData.accountCode) newErrors.accountCode = 'Account code is required';
    if (!formData.accountName) newErrors.accountName = 'Account name is required';
    if (!formData.accountType) newErrors.accountType = 'Account type is required';
    if (!formData.subType) newErrors.subType = 'Sub type is required';
    if (!formData.openingDate) newErrors.openingDate = 'Opening date is required';
    // Unique validation
    if (formData.accountCode) {
      const isCodeUnique = await coaService.validateAccountCode(formData.accountCode, isEdit ? initialData?.accountCode : undefined);
      if (!isCodeUnique) newErrors.accountCode = 'Account code must be unique';
    }
    if (formData.accountName) {
      const isNameUnique = await coaService.validateAccountName(formData.accountName, isEdit ? initialData?.accountName : undefined);
      if (!isNameUnique) newErrors.accountName = 'Account name must be unique';
    }
    // Bank fields validation
    if (formData.subType === 'Bank') {
      if (!formData.bankNameId) newErrors.bankNameId = 'Bank name is required';
      if (!formData.bankBranchId) newErrors.bankBranchId = 'Bank branch is required';
      if (!formData.bankAccountNo) newErrors.bankAccountNo = 'Account number is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (isValid) {
      onSubmit(formData);
    }
  };
  const isBankAccount = formData.subType === 'Bank';
  const isSubAccount = !!formData.parentId;
  return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-99">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-100">
        <Input label="Account Code" required value={formData.accountCode} onChange={e => setFormData({
        ...formData,
        accountCode: e.target.value
      })} error={errors.accountCode} helperText="Follow BERC uniform COA format (e.g., 1000, 1100)" data-id="element-101" />

        <Input label="Account Name" required value={formData.accountName} onChange={e => setFormData({
        ...formData,
        accountName: e.target.value
      })} error={errors.accountName} data-id="element-102" />

        <Input label="Account Name (Somali)" value={formData.accountNameSomali} onChange={e => setFormData({
        ...formData,
        accountNameSomali: e.target.value
      })} data-id="element-103" />

        <Select label="Parent Account" options={parentAccounts} value={formData.parentId || ''} onChange={e => handleParentChange(e.target.value)} helperText="Optional - select to create sub-account" data-id="element-104" />

        <Select label="Account Type" required options={accountTypes.map(t => ({
        value: t,
        label: t
      }))} value={formData.accountType} onChange={e => setFormData({
        ...formData,
        accountType: e.target.value as AccountType,
        subType: ''
      })} error={errors.accountType} disabled={!!formData.parentId} data-id="element-105" />

        <Select label="Sub Type" required options={formData.accountType ? subTypesByType[formData.accountType as AccountType].map(s => ({
        value: s,
        label: s
      })) : []} value={formData.subType} onChange={e => setFormData({
        ...formData,
        subType: e.target.value as AccountSubType
      })} error={errors.subType} disabled={!!formData.parentId || !formData.accountType} data-id="element-106" />

        <DatePicker label="Opening Date" required value={formData.openingDate} onChange={date => setFormData({
        ...formData,
        openingDate: date
      })} error={errors.openingDate} data-id="element-107" />

        <Input label="Prefix" value={formData.prefix} onChange={e => setFormData({
        ...formData,
        prefix: e.target.value
      })} helperText="Used for voucher numbering" data-id="element-108" />
      </div>

      {isSubAccount && <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-109">
          <Select label="Branch" options={[{
        value: 'branch1',
        label: 'Hargeisa Branch'
      }, {
        value: 'branch2',
        label: 'Mogadishu Branch'
      }]} value={formData.branchId || ''} onChange={e => setFormData({
        ...formData,
        branchId: e.target.value
      })} helperText="Only for sub-accounts" data-id="element-110" />

          <Select label="Project" options={[{
        value: 'proj1',
        label: 'Infrastructure Project'
      }, {
        value: 'proj2',
        label: 'Education Initiative'
      }]} value={formData.projectId || ''} onChange={e => setFormData({
        ...formData,
        projectId: e.target.value
      })} helperText="Only for sub-accounts" data-id="element-111" />
        </div>}

      {isBankAccount && <BankFields bankNameId={formData.bankNameId || ''} bankBranchId={formData.bankBranchId || ''} bankAccountNo={formData.bankAccountNo || ''} onBankNameChange={value => setFormData({
      ...formData,
      bankNameId: value
    })} onBankBranchChange={value => setFormData({
      ...formData,
      bankBranchId: value
    })} onBankAccountNoChange={value => setFormData({
      ...formData,
      bankAccountNo: value
    })} errors={errors} data-id="element-112" />}

      <div className="space-y-4" data-id="element-113">
        <div data-id="element-114">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-115">
            Description
          </label>
          <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" rows={3} value={formData.description} onChange={e => setFormData({
          ...formData,
          description: e.target.value
        })} data-id="element-116" />
        </div>

        <div data-id="element-117">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-118">
            Additional Note
          </label>
          <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" rows={2} value={formData.additionalNote} onChange={e => setFormData({
          ...formData,
          additionalNote: e.target.value
        })} data-id="element-119" />
        </div>

        <Select label="Status" required options={[{
        value: 'false',
        label: 'Active'
      }, {
        value: 'true',
        label: 'Inactive'
      }]} value={formData.isInactive.toString()} onChange={e => setFormData({
        ...formData,
        isInactive: e.target.value === 'true'
      })} data-id="element-120" />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t" data-id="element-121">
        <Button type="button" variant="secondary" onClick={onCancel} data-id="element-122">
          Cancel
        </Button>
        <Button type="submit" variant="primary" data-id="element-123">
          {isEdit ? 'Update Account' : 'Create Account'}
        </Button>
      </div>
    </form>;
}